import { ReactElement } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import * as React from 'react';
import { defaultRowsPerPage, pageSizeOptions } from '../../../Settings/config';
import {
  ExternalIdDTO,
  ProjectPersonOtherDTO,
  ProjectToPersonDTO,
  type ReferencedPersonDTO,
  WorkPersonDTO,
  WorkPersonOtherDTO,
} from '../../../api/core';
import { ValidationResult, ValidationResults } from '../../../util/validation/interfaces';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { LinkedPersonIntern } from './parts/LinkedPersonIntern';
import { LinkedPersonExtern } from './parts/LinkedPersonExtern';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { LinkedPersonRow } from './parts/LinkedPersonRow';
import { CheckPermissionFunction } from '../../../Auth/components/PermissionGate';
import { SelectItem } from 'primereact/selectitem';

export type Internal = WorkPersonDTO | ProjectToPersonDTO;
export type External = WorkPersonOtherDTO | ProjectPersonOtherDTO;

export interface ListedInternalPerson {
  internal: true;
  entity: Internal;
  validation: ValidationResults;
}

export interface ListedExternalPerson {
  internal: false;
  entity: External;
  validation: ValidationResults;
}

export type ListedEntity = ListedInternalPerson | ListedExternalPerson;

interface Props {
  internal: Internal[];
  external: External[];
  updateInternal: (person: Internal[]) => void;
  updateExternal: (person: External[]) => void;
  newInternal: () => Internal;
  newExternal: () => External;
  validationInternal: ValidationResult;
  validationExternal: ValidationResult;
  checkPermissionInternal: CheckPermissionFunction;
  checkPermissionExternal: CheckPermissionFunction;
  autoFocus?: boolean;
  enabledExternalIds?: boolean;
  roleOptions: SelectItem[];
}

export const LinkedPerson = (props: Props) => {
  const intl: IntlShape = useIntl();
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(defaultRowsPerPage);
  const [offset, setOffset] = React.useState<number>(0);
  const [addedSequence, setAddedSequence] = React.useState<number>(undefined);

  const updateExternal = (person: External[]): void => {
    setAddedSequence(undefined);
    props.updateExternal(person);
  };

  const updateInternal = (person: Internal[]): void => {
    setAddedSequence(undefined);
    props.updateInternal(person);
  };

  // TODO use effect?
  const listedPersons: ListedEntity[] = [
    ...(props.internal ?? []).map(
      (wp: WorkPersonDTO, idx: number): ListedEntity => ({
        internal: true,
        entity: wp,
        validation: props.validationInternal.children[idx],
      })
    ),

    ...(props.external ?? []).map(
      (wp: WorkPersonOtherDTO, idx: number): ListedEntity => ({
        internal: false,
        entity: wp,
        validation: props.validationExternal.children[idx],
      })
    ),
  ].sort((a: ListedEntity, b: ListedEntity): number => a.entity.sequence - b.entity.sequence);

  const onPageChange = (e: PaginatorPageChangeEvent): void => {
    setRowsPerPage(e.rows);
    setOffset(e.page * e.rows);
  };

  const incrementSequences = <T extends { sequence?: number }>(items: T[], from: number): T[] =>
    items.map((item: T): T => (item.sequence >= from ? { ...item, sequence: item.sequence + 1 } : item));

    const addPerson = (event: React.MouseEvent<any>, newInternal: Internal): void => {
      event.preventDefault();
      setAddedSequence(newInternal.sequence);
      // Update workPersons and append new person
      props.updateInternal([
        ...incrementSequences(props.internal ?? [], newInternal.sequence),
        { ...props.newInternal(), ...newInternal },
      ]);
      // Only resequence workPersonOthers
      props.updateExternal(incrementSequences(props.external ?? [], newInternal.sequence));
    };

    const addPersonOther = (event: React.MouseEvent<any>, newExternal: External): void => {
      event.preventDefault();
      setAddedSequence(newExternal.sequence);
      // Only resequence workPersons
      props.updateInternal(incrementSequences(props.internal ?? [], newExternal.sequence));
      // Update workPersonOthers and append the new person
      props.updateExternal([
        ...incrementSequences(props.external ?? [], newExternal.sequence),
        { ...props.newExternal(), ...newExternal },
      ]);
    };

      const moveDown = (sequence: number): void => {
        const swapDown = <T extends { sequence?: number }>(array: T[], sq: number): T[] => {
          return array.map((item: T): T => {
            if (item.sequence === sq) return { ...item, sequence: item.sequence + 1 };
            if (item.sequence === sq + 1) return { ...item, sequence: item.sequence - 1 };
            return item;
          });
        };

        if (props.internal) {
          props.updateInternal(swapDown<WorkPersonDTO>(props.internal, sequence));
        }
        if (props.external) {
          props.updateExternal(swapDown<WorkPersonOtherDTO>(props.external, sequence));
        }
      };

      const moveUp = (sequence: number): void => {
        const swapUp = <T extends { sequence?: number }>(array: T[], sq: number): T[] => {
          return array.map((item: T): T => {
            if (item.sequence === sq) return { ...item, sequence: item.sequence - 1 };
            if (item.sequence === sq - 1) return { ...item, sequence: item.sequence + 1 };
            return item;
          });
        };

        if (props.internal) {
          props.updateInternal(swapUp<WorkPersonDTO>(props.internal, sequence));
        }
        if (props.external) {
          props.updateExternal(swapUp<WorkPersonOtherDTO>(props.external, sequence));
        }
      };

        const updatePerson = (person: WorkPersonDTO): void => {
          updateInternal(props.internal.map((wp: WorkPersonDTO): WorkPersonDTO => (wp.sequence === person.sequence ? person : wp)));
        };

        const updatePersonOther = (person: WorkPersonOtherDTO): void => {
          updateExternal(props.external.map((wp: WorkPersonOtherDTO): WorkPersonOtherDTO => (wp.sequence === person.sequence ? person : wp)));
        };

          const removePerson = (sequence: number): void => {
            const removeAndResequence = <T extends { sequence?: number }>(items: T[], sq: number): T[] => {
              return items
                .filter((item: T): boolean => item.sequence !== sq)
                .map((item: T): T => (item.sequence > sq ? { ...item, sequence: item.sequence - 1 } : item));
            };

            if (props.internal) {
              props.updateInternal(removeAndResequence(props.internal, sequence));
            }

            if (props.external) {
              props.updateExternal(removeAndResequence(props.external, sequence));
            }
          };
return (
  <div className={'col-12 flex flex-column gap-4'}>
    <Paginator
      first={offset}
      rows={rowsPerPage}
      totalRecords={listedPersons.length}
      rowsPerPageOptions={pageSizeOptions}
      onPageChange={onPageChange}
    />

    {listedPersons
      .slice(offset, offset + rowsPerPage)
      .map((listedPerson: ListedEntity, idx: number, array: ListedEntity[]): ReactElement => {
        const autoFocus: boolean = props.autoFocus && addedSequence === listedPerson.entity.sequence;
        return (
          <div key={listedPerson.entity.sequence}>
            <LinkedPersonRow
              enableExternalIds={props.enabledExternalIds}
              listedPerson={listedPerson}
              first={idx === 0 && offset === 0}
              last={idx === array.length - 1 && offset + rowsPerPage >= listedPersons.length}
              length={array.length}
              onMoveUp={(): void => moveUp(listedPerson.entity.sequence)}
              onMoveDown={(): void => moveDown(listedPerson.entity.sequence)}
              onDelete={(): void => removePerson(listedPerson.entity.sequence)}
              addPerson={addPerson}
              addPersonOther={addPersonOther}
              updateExternalIds={(externalIds: ExternalIdDTO[]): void =>
                props.updateExternal([
                  ...props.external.map(
                    (wp: External): External =>
                      wp.sequence === listedPerson.entity.sequence
                        ? {
                            ...wp,
                            externalIds,
                          }
                        : { ...wp }
                  ),
                ])
              }
              checkPermissionOther={props.checkPermissionExternal}
            >
              {listedPerson.internal ? (
                <LinkedPersonIntern
                  person={listedPerson.entity}
                  validation={listedPerson.validation}
                  autoFocus={autoFocus}
                  checkPermission={props.checkPermissionInternal}
                  onChange={updatePerson}
                  roleOptions={props.roleOptions}
                />
              ) : (
                <LinkedPersonExtern
                  person={listedPerson.entity}
                  validation={listedPerson.validation}
                  autoFocus={autoFocus}
                  checkPermission={props.checkPermissionExternal}
                  onChange={updatePersonOther}
                  roleOptions={props.roleOptions}
                />
              )}
            </LinkedPersonRow>
          </div>
        );
      })}
    {offset + rowsPerPage >= listedPersons.length && (
      <div className={'p-inputgroup h-2rem flex justify-content-end'}>
        <InputText disabled={true} className={'border-none'} />
        <Button
          id={'add-intern-0'}
          className={'border-round border-primary-600'}
          icon={'pi pi-plus'}
          label={intl.formatMessage({ id: 'work.button.text.addPersonIntern' })}
          onClick={(event): void =>
            addPerson(event, {
              sequence: listedPersons.length === 0 ? 1 : Math.max(...listedPersons.map((lp) => lp.entity.sequence)) + 1,
            })
          }
        />
        <Button
          id={'add-extern-0'}
          className={'border-round border-primary-600'}
          icon={'pi pi-plus'}
          label={intl.formatMessage({ id: 'work.button.text.addPersonExtern' })}
          onClick={(event): void =>
            addPersonOther(event, {
              sequence: listedPersons.length === 0 ? 1 : Math.max(...listedPersons.map((lp) => lp.entity.sequence)) + 1,
            })
          }
        />
      </div>
    )}
    <Paginator
      first={offset}
      rows={rowsPerPage}
      totalRecords={listedPersons.length}
      rowsPerPageOptions={pageSizeOptions}
      onPageChange={onPageChange}
    />
  </div>
);
}
